import React, { useState } from 'react'

import { ShieldAlert } from 'lucide-react'

import { Box, Stack, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { Tooltip } from '@repo/ui'

import { summarizeInvalidScans } from 'utils/invalidScanSeverity'

/**
 * VRG-491 — "N scans on this certificate failed validation", as a glyph and a tally.
 *
 * ONE component for both surfaces that show it (the certificates table cell and the
 * certificate page header), so the glyph, the colours and the sentence cannot drift
 * between them (working-conventions §36). The severity rules it renders live in
 * `utils/invalidScanSeverity` and are not restated here.
 *
 * ## Why this is never a badge
 *
 * A badge asserts a STATE. "3 invalid scans" as a badge says *this certificate is
 * invalid*, which is false — the certificate is fine; three taps failed validation,
 * possibly by somebody with no connection to it at all. Tamper is the state on this
 * page and it gets the badge (`TamperBadge`); this is a count of events, so it gets a
 * count.
 *
 * ## Colour is never the only channel
 *
 * Red and amber are pre-attentive, which is the whole reason to use them: one red row
 * among ambers is found while scanning a table, without reading anything. But roughly
 * 8% of men cannot separate the two hues, and WCAG 1.4.1 forbids encoding meaning in
 * colour alone, so severity is carried three times over:
 *
 *   1. colour        — error red for spoof, warning amber for benign
 *   2. FILL          — the spoof glyph is filled, the benign one is outlined
 *   3. tooltip text  — names the tier in words, the final fallback
 *
 * The glyph itself is the SAME shape in both states on purpose: shape carries identity
 * ("an invalid scan happened here"), and changing it would make the two tiers read as
 * two unrelated conditions rather than as degrees of one.
 */

export interface InvalidScanIndicatorProps {
  /** `invalid_scan_count` from the certificate payload. */
  count?: number | null
  /** `invalid_reason_counts` from the certificate payload. */
  reasonCounts?: Record<string, number> | null
  /** Glyph size in px. 14 in a table cell's metadata line, 16 in the page header. */
  size?: number
  /**
   * Called on click. Set on the certificate page, where the indicator opens the
   * invalid-scan popover (`InvalidScanPopover`); omitted in the table, where the whole
   * cell is already a link to the certificate.
   *
   * Receives the event so a caller anchoring a floating surface can take
   * `event.currentTarget` as its `anchorEl` without this component forwarding a ref.
   * Because the trigger is a REAL `<button>` when this is set, keyboard activation
   * (Enter / Space) dispatches a click too, so the anchor is correct for keyboard users
   * as well as mouse — there is no separate key handler to keep in sync.
   */
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
  /**
   * Accessible name for the trigger. The visible content is a glyph and a bare number,
   * so without this a screen reader announces "3, button" — the count with no statement
   * of what is being counted. Only meaningful alongside `onClick`.
   */
  ariaLabel?: string
  /**
   * `aria-haspopup` for the trigger — `'dialog'` when it opens the popover. Left unset in
   * the plain navigate case, where nothing pops up.
   */
  ariaHasPopup?: 'dialog' | 'menu' | 'true'
  /** `aria-expanded` for the trigger, when it controls a popover. */
  ariaExpanded?: boolean
  /**
   * Hold the tooltip closed. Set while the popover is open, so the two floating surfaces
   * do not stack over each other — the popover restates the tooltip's content and adds
   * the actions, so a tooltip beside it is redundant as well as ugly.
   *
   * The tooltip is controlled internally (not merely `disableHoverListener`) because
   * disabling listeners does not CLOSE a tooltip already open at the moment of click,
   * which is exactly the case here: you hover, the tooltip opens, then you click.
   */
  suppressTooltip?: boolean
}

export const InvalidScanIndicator = ({
  count,
  reasonCounts,
  size = 14,
  onClick,
  ariaLabel,
  ariaHasPopup,
  ariaExpanded,
  suppressTooltip = false,
}: InvalidScanIndicatorProps) => {
  const theme = useTheme()
  const summary = summarizeInvalidScans(reasonCounts, count)
  // Controlled so `suppressTooltip` can force it shut. See that prop's note for why
  // `disableHoverListener` is not sufficient.
  const [tooltipOpen, setTooltipOpen] = useState(false)

  // No invalid scans — the overwhelmingly common case. Handled here rather than at each
  // call site so no surface can forget the guard and render a bare "0".
  if (!summary) return null

  const isSpoof = summary.severity === 'spoof'
  const color = isSpoof
    ? theme.palette.error.main
    : theme.palette.warning.main

  const tier = isSpoof
    ? 'A tag was presented that we did not issue, or a proof was replayed.'
    : 'Most likely an old or mangled link rather than a forgery.'

  // Two statements of DIFFERENT KINDS, so they get two blocks rather than one run of
  // prose. Above: what an invalid scan IS (a definition, true of every row in the
  // tally). Below: what actually happened HERE (a finding, specific to this
  // certificate). Collapsing them reads as one paragraph, and the finding, the only
  // part that varies per certificate, is the part that disappears.
  //
  // This used to be a bare `<br />`, which is invisible: the definition already wraps
  // to two lines at any sane tooltip width, so a line break lands among natural wrap
  // points and reads as the third line of the same paragraph. Separation has to be
  // SPACE, not a newline. 8px on top of the 21px line box (14px x 1.5) roughly doubles
  // the within-paragraph leading, which is what makes it read as a break.
  //
  // The LABEL is muted, not the value: the reason itself is the actionable half and
  // stays at full strength. Opacity rather than a palette colour because the tooltip
  // surface inverts between themes (white/dark-text in light, #141417/light-text in
  // dark); a fixed secondary colour would be wrong in one of them.
  const title = (
    <Box>
      <Box>
        {summary.count} {summary.count === 1 ? 'scan' : 'scans'} failed verification.{' '}
        {tier}
      </Box>
      <Box sx={{ mt: '8px' }}>
        <Box component="span" sx={{ opacity: 0.7 }}>
          Most common reason:{' '}
        </Box>
        {summary.topReasonLabel}.
      </Box>
    </Box>
  )

  const interactive = Boolean(onClick)

  return (
    <Tooltip
      title={title}
      placement="top"
      arrow
      // Always controlled — never `open={undefined}` conditionally, which would flip MUI
      // between uncontrolled and controlled mid-life and warn.
      open={tooltipOpen && !suppressTooltip}
      onOpen={() => setTooltipOpen(true)}
      onClose={() => setTooltipOpen(false)}
      // Wider than MUI's 300px default. At 300 the spoof-tier definition is 96
      // characters and breaks to THREE lines, so the tooltip is four ragged lines with
      // the finding stranded at the bottom. 340 pulls the definition back to two and
      // keeps the whole thing to three lines plus the break.
      slotProps={{ tooltip: { sx: { maxWidth: 340 } } }}
    >
      <Stack
        // A REAL `<button>` when it does something, not a `div` wearing `role="button"`.
        // Native semantics bring the tab stop, Enter/Space activation and the AT
        // announcement for free, which is what the hand-rolled `onKeyDown` here used to
        // approximate — and it makes `event.currentTarget` a stable popover anchor on
        // keyboard activation as well as mouse.
        component={interactive ? 'button' : 'div'}
        {...(interactive ? { type: 'button' as const } : {})}
        direction="row"
        spacing={1}
        alignItems="center"
        onClick={
          onClick
            ? (e: React.MouseEvent<HTMLElement>) => {
                // The table cell wraps this in a router Link to the certificate, and the
                // header sits inside a clickable band — in both, an unstopped click would
                // navigate instead of doing what the handler asks.
                e.preventDefault()
                e.stopPropagation()
                onClick(e)
              }
            : undefined
        }
        aria-label={interactive ? ariaLabel : undefined}
        aria-haspopup={interactive ? ariaHasPopup : undefined}
        aria-expanded={interactive ? ariaExpanded : undefined}
        sx={{
          flexShrink: 0,
          cursor: 'pointer',
          ...(interactive && {
            // The UA button chassis, removed — this is a glyph and a tally, not a chip.
            appearance: 'none',
            background: 'none',
            border: 'none',
            font: 'inherit',
            color: 'inherit',
            width: 'fit-content',
            /* DISCOVERABILITY. The visible content reads as an INDICATOR (a status glyph
               plus a number), so nothing about it says "click me" at rest — which is the
               known weak point of putting an action behind it. The hover tint is the
               cheapest honest signal: it costs nothing while idle and, the moment the
               cursor is over it, the element visibly becomes a control.

               The padding + equal negative margin give the tint a body to fill without
               moving the glyph one pixel — the indicator sits in a header row beside a
               badge, so a layout shift on hover would be worse than no affordance. */
            px: '6px',
            py: '3px',
            mx: '-6px',
            my: '-3px',
            borderRadius: '6px',
            transition: theme.transitions.create('background-color', {
              duration: theme.transitions.duration.shortest,
            }),
            '&:hover': { backgroundColor: alpha(color, 0.12) },
          }),
          // A visible ring when focused — the glyph alone would give no focus affordance
          // at all, and this is the only cue a keyboard user gets that it is reachable.
          '&:focus-visible': {
            outline: `2px solid ${color}`,
            outlineOffset: 2,
            borderRadius: '6px',
          },
        }}
      >
        <ShieldAlert
          size={size}
          color={color}
          // The redundant channel. lucide's root <svg> carries fill="none" and its paths
          // inherit it, so a fill passed here is what actually fills the glyph: spoof
          // reads as a filled shield, benign as an outline, with no colour involved.
          //
          // A TINT rather than the solid colour, and that is not a softening — the "!"
          // inside ShieldAlert is drawn as strokes in this same colour, so a solid fill
          // would swallow the alert mark and leave a blank shield. The tint keeps the
          // silhouette filled and the mark legible. Stroke width is deliberately equal
          // in both states so fill is the only difference and cannot read as accidental.
          fill={isSpoof ? alpha(color, 0.32) : 'none'}
          strokeWidth={2}
          style={{ flexShrink: 0 }}
        />
        <Typography
          variant="body4"
          sx={{ color, fontWeight: 600, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}
        >
          {summary.count}
        </Typography>
      </Stack>
    </Tooltip>
  )
}
